import { useMemo } from 'react'
import { z } from 'zod'
import { useLocalStorage } from '@hooks/useLocalStorage.ts'
import { haversineMiles } from '@utils/distance.ts'
import {
	Accessibility,
	type Accessibility as TAccessibility,
	type Activity,
	ActivityType,
	type ActivityType as TActivityType,
	Audience,
	type Audience as TAudience,
	Cost,
	type Cost as TCost,
	WeatherSuit,
	type WeatherSuit as TWeatherSuit,
} from '@hewey/shared'

export const WHERE_FILTER = ['all', 'home', 'place'] as const
export type WhereFilter = (typeof WHERE_FILTER)[number]

// Distance-filter snap stops, in miles. The slider positions are
// [null, ...DISTANCE_STOPS] — null (leftmost) is "Any", i.e. off. Even
// spacing means low values are as easy to pick as high ones.
export const DISTANCE_STOPS = [1, 2, 3, 5, 10, 25] as const

export type ActivityFilter = {
	types: TActivityType[]
	costs: TCost[]
	weathers: TWeatherSuit[]
	audiences: TAudience[]
	accessibilities: TAccessibility[]
	where: WhereFilter
	maxDistanceMiles: number | null
}

export const DEFAULT_FILTER: ActivityFilter = {
	types: [],
	costs: [],
	weathers: [],
	audiences: [],
	accessibilities: [],
	where: 'all',
	maxDistanceMiles: null,
}

// Loose schema for what's in localStorage — older shapes get defaulted
// rather than throwing. Anything unknown collapses to DEFAULT_FILTER.
export const ActivityFilterSchema = z.object({
	types: z.array(ActivityType).default([]),
	costs: z.array(Cost).default([]),
	weathers: z.array(WeatherSuit).default([]),
	audiences: z.array(Audience).default([]),
	accessibilities: z.array(Accessibility).default([]),
	where: z.enum(WHERE_FILTER).default('all'),
	maxDistanceMiles: z.number().positive().nullable().default(null),
})

export const isDefaultFilter = (f: ActivityFilter): boolean =>
	f.types.length === 0 &&
	f.costs.length === 0 &&
	f.weathers.length === 0 &&
	f.audiences.length === 0 &&
	f.accessibilities.length === 0 &&
	f.where === 'all' &&
	f.maxDistanceMiles === null

export const activeFilterCount = (f: ActivityFilter): number =>
	f.types.length +
	f.costs.length +
	f.weathers.length +
	f.audiences.length +
	f.accessibilities.length +
	(f.where === 'all' ? 0 : 1) +
	(f.maxDistanceMiles === null ? 0 : 1)

export const matchesFilter = (
	a: Activity,
	f: ActivityFilter,
	origin: { lat: number; lng: number } | null = null,
): boolean => {
	// Distance is purely geographic: while it's active, only place
	// activities within the radius qualify (home activities have no
	// coordinates). If we don't have the user's position yet — still
	// resolving, denied, or unavailable — the constraint no-ops so the list
	// doesn't flash empty; the dialog explains why.
	if (f.maxDistanceMiles !== null && origin) {
		if (a.location.kind !== 'place') return false
		const miles = haversineMiles(
			origin.lat,
			origin.lng,
			a.location.lat,
			a.location.lng,
		)
		if (miles > f.maxDistanceMiles) return false
	}

	if (f.where === 'home' && a.location.kind !== 'home') return false
	if (f.where === 'place' && a.location.kind !== 'place') return false

	if (f.types.length > 0) {
		if (!a.types.some((t) => f.types.includes(t))) return false
	}

	if (f.costs.length > 0) {
		if (a.cost === null || !f.costs.includes(a.cost)) return false
	}

	// `any` is a wildcard — an activity tagged `any` satisfies every
	// weather filter (rather than only matching the `any` pill).
	if (f.weathers.length > 0) {
		const wildcard = a.weatherSuits.includes('any')
		if (!wildcard && !a.weatherSuits.some((w) => f.weathers.includes(w))) {
			return false
		}
	}

	if (f.audiences.length > 0) {
		if (!a.audience.some((x) => f.audiences.includes(x))) return false
	}

	// AND semantics: filtering by access needs is a hard requirement, so
	// an activity must carry every selected tag (unlike audience, where
	// any match suffices).
	if (f.accessibilities.length > 0) {
		if (!f.accessibilities.every((x) => a.accessibility.includes(x))) {
			return false
		}
	}

	return true
}

const lastVisit = (a: Activity): string | null =>
	a.visits.length > 0 ? (a.visits[a.visits.length - 1] ?? null) : null

const FILTER_KEY = 'hewey.activities.filter'

// Shared filter state for the list and map views — both pages mount
// this hook so toggling between them preserves what the user has
// narrowed to. Stored under a single localStorage key; default filter
// clears the entry rather than writing the all-empty shape.
export const useActivityFilter = (): [
	ActivityFilter,
	(next: ActivityFilter) => void,
] => {
	const [stored, setStored] = useLocalStorage<unknown>(FILTER_KEY)
	const filter = useMemo<ActivityFilter>(() => {
		const parsed = ActivityFilterSchema.safeParse(stored)
		return parsed.success ? parsed.data : DEFAULT_FILTER
	}, [stored])
	const setFilter = (next: ActivityFilter) =>
		setStored(isDefaultFilter(next) ? undefined : next)
	return [filter, setFilter]
}

// "Not done in a while" — never-visited first, then oldest-visited.
// Tiebreak by createdAt newest-first so freshly added activities sit
// near the top of the unvisited pool (handy in the early days when
// nothing has visit history yet).
export const sortByStale = (a: Activity, b: Activity): number => {
	const av = lastVisit(a)
	const bv = lastVisit(b)
	if (av === null && bv === null)
		return b.createdAt.localeCompare(a.createdAt)
	if (av === null) return -1
	if (bv === null) return 1
	return av.localeCompare(bv)
}
